import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import { ErrorHandler, inject, NgModule, Provider } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { TitleStrategy } from '@angular/router';
import { NgbConfig } from '@ng-bootstrap/ng-bootstrap';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { AngularSvgIconModule, SvgLoader } from 'angular-svg-icon';
import { CookieService } from 'ngx-cookie-service';

import { NgxGoogleAnalyticsModule, NgxGoogleAnalyticsRouterModule } from 'ngx-google-analytics';
import { environment } from 'src/environments/environment';
import { AppPageTitleStrategy } from './app-pages-title/app-pages-title';
import { AppPreloadingModule } from './app-preloading/app-preloading.module';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AuthModule } from './auth/auth.module';
import AppSettings from './common/app.settings';
import { SaveDataGuard } from './common/guards/save-data-guard/save-data.guard';
import { ErrorResponseInterceptor } from './common/interceptors/error-response/http-error-interceptor';
import { LoadingInterceptor } from './common/interceptors/loading/loading.interceptor';
import { INTERCEPTOR_CONFIG } from './common/interceptors/tokens';
import { LongOperationModule } from './common/long-operations/long-operations.module';
import { SentryErrorHandler } from './common/services/sentry-error-handler/sentry-error-handler.service';
import { HttpSvgLoader } from './common/services/svg-http-loader.service';
import { ToastGlobalComponent } from './common/toast/toast-global/toast-global.component';
import { AdminModule } from './components/admin/admin.module';
import { ChatModule } from './components/chat/chat.module';
import { TwilioChatEngineModule } from './components/chat/services/twilio/twilio-chat-engine.module';
import { CommunityModule } from './components/community/community.module';
import { ContactUsComponent } from './components/contact-us/contact-us.component';
import { EventNotificationsModule } from './components/event-notifications/event-notifications.module';
import { FilesManagerModule } from './components/files/files-manager.module';
import { MapModule } from './components/map/map.module';
import { NavigationModule } from './components/navigation/navigation.module';
import { PreferencesModule } from './components/preferences/preferences.module';
import { ProfileModule } from './components/profile/profile.module';
import { SearchModule } from './components/search/search.module';
import { StaticModule } from './components/static/static.module';
import { TableConstructorModule } from './components/tables/table-constructor.module';
import { UserRelationRequestModule } from './components/user-relation-requests/relation-requests.module';
import { AppCookieConsentModule } from './cookie-consest.module';
import { FormsConstructorModule } from './forms/forms-constructor.module';
import { HttpParamsPreProcessingModule } from './http-params/http-params-pre-processing.module';
import { NotificationsModule } from './notifications/notifications.module';
import { CoreModule } from './services/core.module';
import { SharedModule } from './shared.module';
import { ApiModule, Configuration } from './swagger-api';
import { MaybeUndefined } from './utils';
import { CardsModule } from './views/cards/cards.module';
import { ViewsModule } from './views/views.module';

class AppNgbConfig {
  public animation = false;
}

const apiConfigFactory: () => Configuration = () =>
  new Configuration({
    basePath: AppSettings.BASE_URL,
  });

const translateLoaderFactory: (http: HttpClient) => TranslateHttpLoader = (http: HttpClient) =>
  new TranslateHttpLoader(http, 'assets/i18n/', '.json');

export const ErrorInterceptorProvider: Provider = {
  provide: HTTP_INTERCEPTORS,
  useClass: ErrorResponseInterceptor,
  multi: true,
};

@NgModule({
  declarations: [AppComponent, ContactUsComponent],
  imports: [
    ToastGlobalComponent,
    BrowserModule,
    AppRoutingModule,
    HttpClientModule,
    AppPreloadingModule,
    AppCookieConsentModule,
    HttpParamsPreProcessingModule,
    SearchModule,
    FilesManagerModule.forRoot(),
    SharedModule,
    ViewsModule,
    ApiModule.forRoot(apiConfigFactory),
    AdminModule.forRoot(),
    AuthModule.forRoot(),
    NavigationModule.forRoot(),
    TableConstructorModule.forRoot(),
    FormsConstructorModule.forRoot(),
    MapModule.forRoot(),
    CoreModule.forRoot(),
    ChatModule,
    StaticModule,
    ProfileModule.forRoot(),
    CommunityModule,
    UserRelationRequestModule,
    NotificationsModule.forRoot(),
    PreferencesModule,
    CardsModule.forRoot(),
    ApiModule.forRoot(apiConfigFactory),
    NgxGoogleAnalyticsModule.forRoot(environment.googleAnalyticsId),
    NgxGoogleAnalyticsRouterModule,
    LongOperationModule,
    TwilioChatEngineModule,
    EventNotificationsModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: translateLoaderFactory,
        deps: [HttpClient],
      },
    }),
    AngularSvgIconModule.forRoot({
      loader: {
        provide: SvgLoader,
        useClass: HttpSvgLoader,
      },
    }),
  ],
  bootstrap: [AppComponent],
  providers: [
    SaveDataGuard,
    CookieService,
    ErrorInterceptorProvider,
    { provide: NgbConfig, useClass: AppNgbConfig },
    { provide: ErrorHandler, useClass: SentryErrorHandler },
    { provide: TitleStrategy, useClass: AppPageTitleStrategy },
    { provide: INTERCEPTOR_CONFIG, useValue: { apiUrl: environment.apiUrl } },
    { provide: HTTP_INTERCEPTORS, useClass: LoadingInterceptor, multi: true },
  ],
})
export class AppModule {
  private translateService = inject(TranslateService);

  public constructor() {
    this.initializeTranslateService();
  }

  private initializeTranslateService(): void {
    this.translateService.addLangs(['en']);
    this.translateService.setDefaultLang('en');

    const langs: string[] = this.translateService.getLangs();
    const browserLang: MaybeUndefined<string> = this.translateService.getBrowserLang();

    if (browserLang && langs.indexOf(browserLang) !== -1) {
      this.translateService.use(browserLang);
    }
  }
}
